import { useState, useEffect, useRef, useCallback } from "react";

const API = "https://api.bloomin.live";
const LS_KEY = "ig_planner_drafts";

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include", ...opts,
    headers: { "Content-Type": "application/json", ...opts.headers },
  });
  if (!res.ok) throw await res.json().catch(() => ({ error: "Request failed" }));
  return res.json();
}

function uid() {
  return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function loadDraftsFromStorage() {
  try { const raw = localStorage.getItem(LS_KEY); return raw ? JSON.parse(raw) : []; }
  catch { return []; }
}

function saveDraftsToStorage(drafts) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(drafts)); }
  catch (e) { console.warn("localStorage quota exceeded:", e); }
}

// ─── Drag/Touch Hook ───────────────────────────────────────────────────────────
// Works on both desktop (mouse drag) and mobile (touch).
// Returns props to spread onto each draggable card, plus state for styling.

function useDragReorder({ items, setItems, draftCount }) {
  const draggedIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);

  // Touch state
  const touchStartPos = useRef(null);
  const currentTouchIndex = useRef(null);
  const gridRef = useRef(null);

  // ── Desktop (HTML5 drag) ──────────────────────────────────────────────────

  const onDragStart = useCallback((e, index) => {
    if (index >= draftCount) return;
    draggedIndex.current = index;
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = "move";
    const ghost = document.createElement("div");
    ghost.style.cssText = "width:1px;height:1px;position:fixed;top:-9999px";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  }, [draftCount]);

  const onDragOver = useCallback((e, index) => {
    e.preventDefault();
    if (draggedIndex.current === null || index >= draftCount) return;
    if (index !== draggedIndex.current) setDragOverIndex(index);
  }, [draftCount]);

  const onDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const from = draggedIndex.current;
    if (from === null || from === dropIndex || dropIndex >= draftCount) return;
    setItems(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    draggedIndex.current = null;
    setDraggedIndex_state(null);
    setDragOverIndex(null);
    setDraggingIndex(null);
  }, [draftCount, setItems]);

  // helper so we can clear draggingIndex from onDrop
  const [, setDraggedIndex_state] = useState(null);

  const onDragEnd = useCallback(() => {
    draggedIndex.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  }, []);

  // ── Mobile (Touch) ────────────────────────────────────────────────────────

  const getIndexFromPoint = useCallback((x, y) => {
    if (!gridRef.current) return null;
    const cells = gridRef.current.querySelectorAll("[data-card-index]");
    for (const cell of cells) {
      const rect = cell.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return parseInt(cell.getAttribute("data-card-index"), 10);
      }
    }
    return null;
  }, []);

  const onTouchStart = useCallback((e, index) => {
    if (index >= draftCount) return;
    // Single finger only
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    touchStartPos.current = { x: t.clientX, y: t.clientY };
    draggedIndex.current = index;
    currentTouchIndex.current = index;
    setDraggingIndex(index);
  }, [draftCount]);

  const onTouchMove = useCallback((e) => {
    if (draggedIndex.current === null) return;
    e.preventDefault(); // prevent page scroll while dragging
    const t = e.touches[0];
    const overIndex = getIndexFromPoint(t.clientX, t.clientY);
    if (overIndex !== null && overIndex < draftCount && overIndex !== draggedIndex.current) {
      currentTouchIndex.current = overIndex;
      setDragOverIndex(overIndex);
    }
  }, [draftCount, getIndexFromPoint]);

  const onTouchEnd = useCallback(() => {
    const from = draggedIndex.current;
    const to = currentTouchIndex.current;
    if (from !== null && to !== null && from !== to && to < draftCount) {
      setItems(prev => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    }
    draggedIndex.current = null;
    currentTouchIndex.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  }, [draftCount, setItems]);

  return {
    gridRef,
    draggingIndex,
    dragOverIndex,
    cardProps: (index) => ({
      "data-card-index": index,
      // Desktop
      draggable: index < draftCount,
      onDragStart: index < draftCount ? (e) => onDragStart(e, index) : undefined,
      onDragOver: (e) => onDragOver(e, index),
      onDrop: (e) => onDrop(e, index),
      onDragEnd: index < draftCount ? onDragEnd : undefined,
      // Mobile
      onTouchStart: index < draftCount ? (e) => onTouchStart(e, index) : undefined,
      onTouchMove: index < draftCount ? onTouchMove : undefined,
      onTouchEnd: index < draftCount ? onTouchEnd : undefined,
    }),
  };
}

const IgIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="22" height="22">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);

const GripIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" opacity="0.5">
    <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
    <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
    <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
  </svg>
);

// ─── Dev Login Page ────────────────────────────────────────────────────────────

function DevLoginPage({ onLogin }) {
  const [userId, setUserId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!userId.trim() || !accessToken.trim()) {
      setError("Both User ID and Access Token are required.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch("/auth/dev-login", {
        method: "POST",
        body: JSON.stringify({ userId: userId.trim(), accessToken: accessToken.trim() }),
      });
      onLogin(data.user);
    } catch (err) {
      setError(err.error || "Login failed. Check your credentials and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#0f0f0f 0%,#1a1a2e 50%,#0f0f0f 100%)", fontFamily: "'DM Sans',sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{`@keyframes spin { to { transform:rotate(360deg) } }`}</style>
      <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(131,58,180,0.15) 0%,transparent 70%)", top: -100, left: -100, pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(253,29,29,0.1) 0%,transparent 70%)", bottom: -50, right: -50, pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 420, padding: "0 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", marginBottom: 20, boxShadow: "0 20px 60px rgba(131,58,180,0.4)" }}>
            <IgIcon size={26} />
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: "#fff", margin: "0 0 8px", letterSpacing: "-0.03em" }}>Developer Login</h1>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", margin: 0, lineHeight: 1.6 }}>
            Enter your test credentials from the{" "}
            <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" style={{ color: "rgba(131,58,180,0.8)", textDecoration: "none" }}>Facebook App Dashboard</a>.
          </p>
        </div>

        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: "26px 24px", backdropFilter: "blur(10px)" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", color: "#666", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", marginBottom: 7 }}>INSTAGRAM USER ID</label>
            <input
              type="text"
              value={userId}
              onChange={e => setUserId(e.target.value)}
              placeholder="e.g. 17841447026235758"
              style={{ width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, color: "#fff", fontSize: 13, padding: "10px 12px", outline: "none", fontFamily: "monospace", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", color: "#666", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", marginBottom: 7 }}>ACCESS TOKEN</label>
            <textarea
              value={accessToken}
              onChange={e => setAccessToken(e.target.value)}
              placeholder="Paste your access token here…"
              rows={3}
              style={{ width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, color: "#fff", fontSize: 12, padding: "10px 12px", outline: "none", fontFamily: "monospace", resize: "none", boxSizing: "border-box" }}
            />
          </div>

          {error && (
            <div style={{ background: "rgba(253,29,29,0.08)", border: "1px solid rgba(253,29,29,0.2)", borderRadius: 8, color: "#ff6b6b", fontSize: 12, padding: "9px 12px", marginBottom: 14 }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{ width: "100%", padding: "11px", borderRadius: 11, border: "none", background: loading ? "rgba(131,58,180,0.35)" : "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit" }}
          >
            {loading
              ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Verifying…</>
              : <><IgIcon size={16} />Sign In</>
            }
          </button>
        </div>

        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.18)", textAlign: "center", marginTop: 18, lineHeight: 1.6 }}>
          Generate a token via <strong style={{ color: "rgba(255,255,255,0.3)" }}>Facebook for Developers → your app → Instagram Graph API → Generate Token</strong>.
        </p>
      </div>
    </div>
  );
}

// ─── Production Login Page ─────────────────────────────────────────────────────

function LoginPage() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#0f0f0f 0%,#1a1a2e 50%,#0f0f0f 100%)", fontFamily: "'DM Sans',sans-serif", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(131,58,180,0.15) 0%,transparent 70%)", top: -100, left: -100, pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(253,29,29,0.1) 0%,transparent 70%)", bottom: -50, right: -50, pointerEvents: "none" }} />
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 400, padding: "0 24px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 76, height: 76, borderRadius: 20, background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", marginBottom: 28, boxShadow: "0 20px 60px rgba(131,58,180,0.4)" }}>
          <IgIcon size={28} />
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 700, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.1 }}>Plan your grid.<br />Preview the future.</h1>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", margin: "0 0 36px", lineHeight: 1.65 }}>Upload upcoming posts, arrange them in your grid, and preview exactly how your profile will look before you hit publish.</p>
        <a href={`${API}/auth/instagram`} style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "13px 26px", background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", color: "#fff", borderRadius: 12, textDecoration: "none", fontWeight: 600, fontSize: 14, boxShadow: "0 8px 28px rgba(131,58,180,0.35)", transition: "transform 0.2s,box-shadow 0.2s" }}
          onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 40px rgba(131,58,180,0.5)"; }}
          onMouseOut={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 8px 28px rgba(131,58,180,0.35)"; }}
        >
          <IgIcon size={18} /> Continue with Instagram
        </a>
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.18)", marginTop: 20 }}>We only read your photos. We never post without your action.</p>
      </div>
    </div>
  );
}

// ─── Publish Modal ─────────────────────────────────────────────────────────────

function PublishModal({ draft, onConfirm, onCancel }) {
  const [caption, setCaption] = useState(draft.caption || "");
  const [publishing, setPublishing] = useState(false);
  const [done, setDone] = useState(false);

  const handlePublish = async () => {
    setPublishing(true);
    await new Promise(r => setTimeout(r, 1800));
    setPublishing(false);
    setDone(true);
    setTimeout(() => onConfirm(draft.id, caption), 900);
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onCancel()} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, width: "100%", maxWidth: 420, overflow: "hidden", boxShadow: "0 40px 80px rgba(0,0,0,0.8)", animation: "modalIn 0.25s cubic-bezier(0.34,1.56,0.64,1)" }}>
        <div style={{ position: "relative", aspectRatio: "1", background: "#0a0a0a" }}>
          <img src={draft.previewUrl} alt="Draft" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          {done && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.65)" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 12 }}>✓</div>
              <span style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>Published!</span>
            </div>
          )}
        </div>
        <div style={{ padding: "20px 22px 24px" }}>
          <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 700, margin: "0 0 3px", letterSpacing: "-0.02em" }}>Publish to Instagram</h3>
          <p style={{ color: "#555", fontSize: 12, margin: "0 0 14px" }}>This will post your draft to your profile.</p>
          <label style={{ display: "block", color: "#777", fontSize: 11, marginBottom: 5, letterSpacing: "0.05em" }}>CAPTION</label>
          <textarea value={caption} onChange={e => setCaption(e.target.value)} placeholder="Write a caption…" rows={3} style={{ width: "100%", background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, color: "#fff", fontSize: 13, padding: "9px 11px", resize: "none", fontFamily: "inherit", outline: "none", marginBottom: 14 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onCancel} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.09)", background: "transparent", color: "#777", fontSize: 13, cursor: "pointer" }}>Cancel</button>
            <button onClick={handlePublish} disabled={publishing || done} style={{ flex: 2, padding: "10px", borderRadius: 10, border: "none", background: publishing || done ? "rgba(131,58,180,0.35)" : "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: publishing || done ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              {publishing ? <><div style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Publishing…</> : done ? "Done ✓" : <><SendIcon />Publish Now</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Photo Card ────────────────────────────────────────────────────────────────

function PhotoCard({ item, index, dragProps, isDraggedOver, isBeingDragged, onPublish, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const isDraft = item.isDraft;
  const imgSrc = isDraft ? item.previewUrl : (item.media_type === "VIDEO" ? item.thumbnail_url : item.media_url);

  return (
    <div
      {...dragProps}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative", aspectRatio: "1", overflow: "hidden",
        cursor: isDraft ? "grab" : "default",
        transition: "transform 0.18s, opacity 0.18s",
        transform: isBeingDragged ? "scale(0.94) rotate(-1.5deg)" : isDraggedOver ? "scale(1.02)" : "scale(1)",
        opacity: isBeingDragged ? 0.4 : 1,
        outline: isDraggedOver ? "2px solid #833ab4" : isDraft ? "1.5px solid rgba(252,180,69,0.45)" : "none",
        outlineOffset: isDraggedOver ? "-2px" : "-1.5px",
        background: "#111",
        userSelect: "none",
        // Prevent iOS long-press callout on images
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
    >
      {imgSrc
        ? <img src={imgSrc} alt={item.caption || "Photo"} draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none" }} />
        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#1a1a1a", color: "#333", fontSize: 11 }}>No preview</div>
      }
      {isDraft && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, padding: "7px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(180deg,rgba(0,0,0,0.75) 0%,transparent 100%)" }}>
          <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.12em", color: "#fcb045", background: "rgba(252,180,69,0.15)", border: "1px solid rgba(252,180,69,0.3)", borderRadius: 3, padding: "2px 5px" }}>DRAFT</span>
          <GripIcon />
        </div>
      )}
      {isDraft && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.58)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 7, opacity: hovered ? 1 : 0, transition: "opacity 0.16s", backdropFilter: "blur(3px)" }}>
          <button onClick={() => onPublish(item)} style={{ background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", border: "none", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 600, padding: "8px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, boxShadow: "0 4px 16px rgba(131,58,180,0.5)" }}>
            <SendIcon /> Publish
          </button>
          <button onClick={() => onDelete(item.id)} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, color: "#ccc", fontSize: 11, padding: "6px 13px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            <TrashIcon /> Remove
          </button>
        </div>
      )}
      {!isDraft && hovered && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.2)", display: "flex", alignItems: "flex-end", justifyContent: "flex-end", padding: 8 }}>
          <div style={{ background: "rgba(0,0,0,0.6)", borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}><LockIcon /></div>
        </div>
      )}
      {isDraggedOver && <div style={{ position: "absolute", inset: 0, background: "rgba(131,58,180,0.14)", pointerEvents: "none" }} />}
      <div style={{ position: "absolute", bottom: 6, left: 7, fontSize: 9, fontWeight: 700, color: isDraft ? "rgba(252,180,69,0.8)" : "rgba(255,255,255,0.3)", letterSpacing: "0.04em" }}>{index + 1}</div>
    </div>
  );
}

// ─── Floating Add Button ───────────────────────────────────────────────────────

function FloatingAddButton({ onUpload }) {
  const [expanded, setExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const processFiles = useCallback((files) => {
    const images = Array.from(files).filter(f => f.type.startsWith("image/"));
    images.forEach(file => {
      const reader = new FileReader();
      reader.onload = e => {
        onUpload({ id: uid(), isDraft: true, previewUrl: e.target.result, caption: "", uploadedAt: new Date().toISOString(), media_type: "IMAGE", fileName: file.name });
      };
      reader.readAsDataURL(file);
    });
    setExpanded(false);
    setIsDragging(false);
  }, [onUpload]);

  return (
    <>
      {expanded && <div onClick={() => setExpanded(false)} style={{ position: "fixed", inset: 0, zIndex: 298, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />}
      {expanded && (
        <div
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={e => { e.preventDefault(); processFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          style={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", zIndex: 299, width: "min(420px, calc(100vw - 48px))", background: isDragging ? "rgba(131,58,180,0.18)" : "rgba(18,18,18,0.98)", border: `2px dashed ${isDragging ? "#a855f7" : "rgba(255,255,255,0.14)"}`, borderRadius: 18, padding: "32px 24px", textAlign: "center", boxShadow: "0 24px 60px rgba(0,0,0,0.7)", animation: "panelUp 0.22s cubic-bezier(0.34,1.56,0.64,1)", cursor: "pointer" }}
        >
          <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => processFiles(e.target.files)} />
          <div style={{ fontSize: 32, marginBottom: 10 }}>{isDragging ? "📥" : "🖼️"}</div>
          <p style={{ color: isDragging ? "#c084fc" : "#fff", fontWeight: 600, fontSize: 15, margin: "0 0 5px" }}>{isDragging ? "Drop to add" : "Add draft photos"}</p>
          <p style={{ color: "#555", fontSize: 12, margin: 0 }}>{isDragging ? "Release to upload" : "Click to browse · or drag images here"}</p>
        </div>
      )}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 300, width: expanded ? 52 : "auto", height: 52, paddingLeft: expanded ? 0 : 22, paddingRight: expanded ? 0 : 22, borderRadius: 26, border: "none", background: expanded ? "rgba(255,255,255,0.09)" : "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: expanded ? "none" : "0 8px 28px rgba(131,58,180,0.5)", transition: "all 0.22s cubic-bezier(0.34,1.56,0.64,1)", letterSpacing: "-0.01em", whiteSpace: "nowrap" }}
      >
        <span style={{ display: "inline-flex", transition: "transform 0.25s", transform: expanded ? "rotate(45deg)" : "rotate(0deg)" }}><PlusIcon /></span>
        {!expanded && "Add draft"}
      </button>
    </>
  );
}

// ─── Gallery Page ──────────────────────────────────────────────────────────────

// ─── Account Switcher ──────────────────────────────────────────────────────────

function AccountSwitcher({ user, accounts, onSwitch, onLogout, onAddAccount, isProduction }) {
  const [open, setOpen] = useState(false);
  const others = accounts.filter(a => a.id !== user?.id);

  return (
    <div style={{ position: "relative" }}>
      {/* Trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, padding: "5px 10px", cursor: "pointer", color: "#fff" }}
      >
        <div style={{ width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
          {user?.username?.[0]?.toUpperCase() || "?"}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>@{user?.username}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10" style={{ opacity: 0.4, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 490 }} />
          <div style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 500, background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, minWidth: 220, padding: "8px", boxShadow: "0 16px 48px rgba(0,0,0,0.7)", animation: "menuIn 0.15s ease" }}>
            <style>{`@keyframes menuIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:none } }`}</style>

            {/* Active account */}
            <div style={{ padding: "8px 10px 6px", borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 6 }}>
              <p style={{ margin: 0, fontSize: 10, color: "#555", letterSpacing: "0.07em", fontWeight: 700 }}>ACTIVE</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                  {user?.username?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#fff" }}>@{user?.username}</p>
                  {user?.media_count != null && <p style={{ margin: 0, fontSize: 11, color: "#555" }}>{user.media_count} posts</p>}
                </div>
                <div style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#4ade80", flexShrink: 0 }} />
              </div>
            </div>

            {/* Other accounts */}
            {others.length > 0 && (
              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 6, paddingBottom: 6 }}>
                <p style={{ margin: "4px 10px 6px", fontSize: 10, color: "#555", letterSpacing: "0.07em", fontWeight: 700 }}>SWITCH TO</p>
                {others.map(acc => (
                  <button
                    key={acc.id}
                    onClick={() => { onSwitch(acc.id); setOpen(false); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: "transparent", border: "none", cursor: "pointer", borderRadius: 8, color: "#ccc" }}
                    onMouseOver={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                    onMouseOut={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                      {acc.username?.[0]?.toUpperCase()}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>@{acc.username}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Add account */}
            <button
              onClick={() => { onAddAccount(); setOpen(false); }}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: "transparent", border: "none", cursor: "pointer", borderRadius: 8, color: "#888" }}
              onMouseOver={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
              onMouseOut={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ width: 26, height: 26, borderRadius: "50%", border: "1.5px dashed rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </div>
              <span style={{ fontSize: 13 }}>Add account</span>
            </button>

            {/* Logout */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: 6, paddingTop: 6 }}>
              <button
                onClick={() => { onLogout(); setOpen(false); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: "transparent", border: "none", cursor: "pointer", borderRadius: 8, color: "#ff6b6b" }}
                onMouseOver={e => e.currentTarget.style.background = "rgba(255,100,100,0.07)"}
                onMouseOut={e => e.currentTarget.style.background = "transparent"}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                <span style={{ fontSize: 13 }}>Log out @{user?.username}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Add Account Modal ─────────────────────────────────────────────────────────

function AddAccountModal({ onAdd, onCancel, isProduction }) {
  const [userId, setUserId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!userId.trim() || !accessToken.trim()) { setError("Both fields are required."); return; }
    setLoading(true); setError(null);
    try {
      const data = await apiFetch("/auth/dev-login", { method: "POST", body: JSON.stringify({ userId: userId.trim(), accessToken: accessToken.trim() }) });
      onAdd(data.user, data.accounts);
    } catch (err) {
      setError(err.error || "Login failed.");
    } finally { setLoading(false); }
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onCancel()} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, width: "100%", maxWidth: 400, padding: "28px 24px", boxShadow: "0 40px 80px rgba(0,0,0,0.8)" }}>
        <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 4px", letterSpacing: "-0.02em" }}>Add another account</h3>
        <p style={{ color: "#555", fontSize: 12, margin: "0 0 20px" }}>Enter credentials for the account you want to add.</p>

        <label style={{ display: "block", color: "#666", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", marginBottom: 6 }}>INSTAGRAM USER ID</label>
        <input type="text" value={userId} onChange={e => setUserId(e.target.value)} placeholder="e.g. 17841447026235758" style={{ width: "100%", background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, color: "#fff", fontSize: 13, padding: "9px 11px", outline: "none", fontFamily: "monospace", marginBottom: 14, boxSizing: "border-box" }} />

        <label style={{ display: "block", color: "#666", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", marginBottom: 6 }}>ACCESS TOKEN</label>
        <textarea value={accessToken} onChange={e => setAccessToken(e.target.value)} placeholder="Paste your access token here…" rows={3} style={{ width: "100%", background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, color: "#fff", fontSize: 12, padding: "9px 11px", outline: "none", fontFamily: "monospace", resize: "none", marginBottom: 16, boxSizing: "border-box" }} />

        {error && <div style={{ background: "rgba(253,29,29,0.08)", border: "1px solid rgba(253,29,29,0.2)", borderRadius: 8, color: "#ff6b6b", fontSize: 12, padding: "9px 12px", marginBottom: 14 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.09)", background: "transparent", color: "#777", fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading} style={{ flex: 2, padding: "10px", borderRadius: 10, border: "none", background: loading ? "rgba(131,58,180,0.35)" : "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: loading ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontFamily: "inherit" }}>
            {loading ? <><div style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />Adding…</> : <><IgIcon size={14} />Add account</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function GalleryPage({ user, accounts, onLogout, onSwitchAccount, isProduction }) {
  const [published, setPublished] = useState([]);
  const [drafts, setDrafts] = useState(() => loadDraftsFromStorage());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [publishTarget, setPublishTarget] = useState(null);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [localAccounts, setLocalAccounts] = useState(accounts || []);

  // When user switches, clear published feed and reload
  const prevUserId = useRef(user?.id);
  useEffect(() => {
    if (user?.id !== prevUserId.current) {
      prevUserId.current = user?.id;
      setPublished([]);
      setLoading(true);
    }
  }, [user?.id]);

  useEffect(() => { saveDraftsToStorage(drafts); }, [drafts]);

  const allItems = [...drafts, ...published];

  // ── Touch/Drag reorder hook ────────────────────────────────────────────────
  const { gridRef, draggingIndex, dragOverIndex, cardProps } = useDragReorder({
    items: drafts,
    setItems: setDrafts,
    draftCount: drafts.length,
  });

  const fetchMedia = useCallback(async (after = null) => {
    try {
      const params = new URLSearchParams({ limit: 24 });
      if (after) params.set("after", after);
      const data = await apiFetch(`/api/media?${params}`);
      if (after) setPublished(prev => [...prev, ...data.media]);
      else setPublished(data.media || []);
      setPagination(data.pagination || {});
    } catch (err) {
      setError(err.error || "Failed to load photos");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = useCallback((draft) => { setDrafts(prev => [draft, ...prev]); }, []);

  const handleConfirmPublish = async (draftId, caption) => {
    const draft = drafts.find(d => d.id === draftId);
    if (!draft) return;

    try {
      let fileToUpload = draft.file;
      if (!fileToUpload) {
        const res = await fetch(draft.previewUrl);
        const blob = await res.blob();
        fileToUpload = new File([blob], draft.fileName || "photo.jpg", { type: blob.type });
      }

      const form = new FormData();
      form.append("image", fileToUpload);
      const uploadRes = await fetch(`${API}/api/publish/upload`, {
        method: "POST",
        credentials: "include",
        body: form,
      }).then(r => r.json());

      if (uploadRes.error) throw new Error(uploadRes.error);

      const publishRes = await fetch(`${API}/api/publish`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl: uploadRes.url, caption }),
      }).then(r => r.json());

      if (publishRes.error) throw new Error(publishRes.error);

      setPublished(prev => [{
        ...draft,
        id: publishRes.id,
        isDraft: false,
        caption,
        media_url: draft.previewUrl,
        timestamp: new Date().toISOString(),
      }, ...prev]);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
    } catch (err) {
      console.error("Publish failed:", err);
      alert(`Publish failed: ${err.message}`);
    } finally {
      setPublishTarget(null);
    }
  };

  const handleDeleteDraft = (draftId) => setDrafts(prev => prev.filter(d => d.id !== draftId));

  const handleLogout = () => onLogout();

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0a0a" }}>
      <div>
        <div style={{ width: 36, height: 36, border: "2px solid #222", borderTop: "2px solid #833ab4", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
        <p style={{ color: "#444", fontSize: 13, fontFamily: "sans-serif", textAlign: "center" }}>Loading your profile…</p>
      </div>
      <style>{`@keyframes spin { to { transform:rotate(360deg) } }`}</style>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", fontFamily: "'DM Sans',sans-serif", paddingBottom: 100 }}>
      <style>{`
        @keyframes spin    { to { transform:rotate(360deg) } }
        @keyframes panelUp { from { opacity:0; transform:translateX(-50%) translateY(20px) } to { opacity:1; transform:translateX(-50%) translateY(0) } }
        @keyframes modalIn { from { opacity:0; transform:scale(0.92) translateY(14px) } to { opacity:1; transform:scale(1) translateY(0) } }
        * { box-sizing:border-box; }
        textarea { box-sizing:border-box; }
      `}</style>

      <header style={{ position: "sticky", top: 0, zIndex: 200, background: "rgba(10,10,10,0.92)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "0 20px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: "linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <IgIcon size={16} />
          </div>
          {drafts.length > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#fcb045", background: "rgba(252,180,69,0.1)", border: "1px solid rgba(252,180,69,0.2)", borderRadius: 6, padding: "3px 8px" }}>
              {drafts.length} draft{drafts.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <AccountSwitcher
          user={user}
          accounts={localAccounts}
          onSwitch={onSwitchAccount}
          onLogout={handleLogout}
          onAddAccount={() => setAddAccountOpen(true)}
          isProduction={isProduction}
        />
      </header>

      <div style={{ display: "flex", gap: 16, padding: "8px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, color: "rgba(252,180,69,0.65)", fontSize: 11 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, border: "1.5px solid rgba(252,180,69,0.55)", display: "inline-block" }} />
          Draft — drag to reorder · hover to publish
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#3a3a3a", fontSize: 11 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, border: "1.5px solid #282828", display: "inline-block" }} />
          Published — locked
        </div>
      </div>

      {error && (
        <div style={{ maxWidth: 340, margin: "16px auto", padding: "10px 14px", background: "rgba(253,29,29,0.08)", border: "1px solid rgba(253,29,29,0.18)", borderRadius: 9, color: "#ff6b6b", fontSize: 12, textAlign: "center" }}>{error}</div>
      )}

      <main style={{ maxWidth: 540, margin: "0 auto" }}>
        {allItems.length === 0 && !error ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#2a2a2a" }}>
            <p style={{ fontSize: 14, marginBottom: 6 }}>Your grid is empty.</p>
            <p style={{ fontSize: 12 }}>Tap "Add draft" below to start planning.</p>
          </div>
        ) : (
          // Attach gridRef here so touch hit-testing can find card positions
          <div
            ref={gridRef}
            style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}
          >
            {allItems.map((item, index) => (
              <PhotoCard
                key={item.id}
                item={item}
                index={index}
                dragProps={cardProps(index)}
                isDraggedOver={dragOverIndex === index}
                isBeingDragged={draggingIndex === index}
                onPublish={setPublishTarget}
                onDelete={handleDeleteDraft}
              />
            ))}
          </div>
        )}

        {pagination.hasNext && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <button onClick={() => { setLoadingMore(true); fetchMedia(pagination.cursors?.after); }} disabled={loadingMore} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#666", borderRadius: 9, padding: "9px 24px", fontSize: 12, cursor: "pointer" }}>
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </main>

      <FloatingAddButton onUpload={handleUpload} />

      {publishTarget && (
        <PublishModal draft={publishTarget} onConfirm={handleConfirmPublish} onCancel={() => setPublishTarget(null)} />
      )}

      {addAccountOpen && (
        <AddAccountModal
          isProduction={isProduction}
          onAdd={(newUser, updatedAccounts) => {
            setLocalAccounts(updatedAccounts || []);
            setAddAccountOpen(false);
          }}
          onCancel={() => setAddAccountOpen(false)}
        />
      )}
    </div>
  );
}

// ─── App Root ──────────────────────────────────────────────────────────────────

export default function App() {
  const [authState, setAuthState] = useState({
    loading: true, authenticated: false, user: null,
    accounts: [], isProduction: true,
  });

  const refreshStatus = useCallback(async () => {
    try {
      const d = await apiFetch("/auth/status");
      setAuthState({
        loading: false,
        authenticated: d.authenticated,
        user: d.user || null,
        accounts: d.accounts || [],
        isProduction: d.isProduction ?? true,
      });
    } catch {
      setAuthState({ loading: false, authenticated: false, user: null, accounts: [], isProduction: true });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth") || params.get("error")) window.history.replaceState({}, "", window.location.pathname);
    refreshStatus();
  }, [refreshStatus]);

  const handleSwitch = useCallback(async (userId) => {
    const d = await apiFetch("/auth/switch", { method: "POST", body: JSON.stringify({ userId }) });
    setAuthState(prev => ({ ...prev, user: d.user, accounts: d.accounts, authenticated: true }));
  }, []);

  const handleLogout = useCallback(async () => {
    const d = await apiFetch("/auth/logout", { method: "POST" }).catch(() => ({ authenticated: false, accounts: [] }));
    if (d.authenticated) {
      setAuthState(prev => ({ ...prev, user: d.user, accounts: d.accounts, authenticated: true }));
    } else {
      setAuthState(prev => ({ ...prev, authenticated: false, user: null, accounts: [] }));
    }
  }, []);

  if (authState.loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0a0a" }}>
      <div style={{ width: 26, height: 26, border: "2px solid #1a1a1a", borderTop: "2px solid #833ab4", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform:rotate(360deg) } }`}</style>
    </div>
  );

  if (!authState.authenticated) {
    if (!authState.isProduction) {
      return (
        <DevLoginPage
          onLogin={user => {
            // After dev login, re-fetch status to pick up full accounts list
            apiFetch("/auth/status").then(d =>
              setAuthState({ loading: false, authenticated: d.authenticated, user: d.user || null, accounts: d.accounts || [], isProduction: d.isProduction ?? true })
            );
          }}
        />
      );
    }
    return <LoginPage />;
  }

  return (
    <GalleryPage
      user={authState.user}
      accounts={authState.accounts}
      onLogout={handleLogout}
      onSwitchAccount={handleSwitch}
      isProduction={authState.isProduction}
    />
  );
}